/**
 * Un archivo de la base (ventas o clientes) en el paso «Subir»: elegirlo o soltarlo acá, cuánto llegó al servidor y
 * cambiarlo. La subida la maneja la página (`SubirBasePage`); acá sólo se muestra.
 */

import { useId, useRef, useState, type DragEvent, type ReactNode } from "react";
import { Check, Loader2, RefreshCw, Upload } from "lucide-react";
import type { ArchivoEnCarga } from "../types";
import { avance } from "../logic/subida";

/** Lo que se eligió en esta pantalla (lo que tiene el servidor viene aparte). */
export interface ArchivoLocal {
  nombre: string;
  subiendo: boolean;
  avance: number;
  error: string;
}

/**
 * Lo que ofrece el selector: lo que lee el backend (`lector.service.ts`, que decide por los bytes y no por el nombre):
 * Excel (.xlsx, el .xls viejo y la planilla XML de 2003), CSV y el HTML que algunos sistemas exportan como planilla.
 */
const ACEPTA = ".xlsx,.xls,.xml,.csv,.html,.htm";

export function ZonaArchivo(p: {
  titulo: string;
  ayuda: ReactNode;
  local: ArchivoLocal | undefined;
  servidor: ArchivoEnCarga | undefined;
  deshabilitada: boolean;
  onArchivo: (f: File | null | undefined) => void;
  onReintentar?: () => void;
}) {
  const idTitulo = useId();
  const input = useRef<HTMLInputElement>(null);
  const [arrastrando, setArrastrando] = useState(false);
  const { local, servidor } = p;
  const subiendo = !!local?.subiendo;
  const error = !subiendo ? local?.error || "" : "";
  const completo = !subiendo && !error && !!servidor?.completo;
  const aMedias = !subiendo && !error && !!servidor && !servidor.completo;
  const nombre = local?.nombre || servidor?.nombre || "";

  const onDrop = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    setArrastrando(false);
    if (!p.deshabilitada) p.onArchivo(e.dataTransfer?.files?.[0]);
  };

  const borde = completo
    ? "border border-emerald-200 bg-emerald-50/60"
    : arrastrando
      ? "border-2 border-dashed border-violet-400 bg-violet-50"
      : "border-2 border-dashed border-violet-200 bg-violet-50/30";

  return (
    <section
      aria-labelledby={idTitulo}
      data-testid={`zona-${p.titulo.toLowerCase()}`}
      onDragOver={(e) => { e.preventDefault(); setArrastrando(true); }}
      onDragLeave={() => setArrastrando(false)}
      onDrop={onDrop}
      className={`flex-1 basis-[300px] min-w-0 rounded-2xl p-5 space-y-2.5 transition-colors ${borde}`}
    >
      <div className="flex items-center gap-2.5">
        <span className={`w-8 h-8 rounded-full flex items-center justify-center ${completo ? "bg-emerald-600 text-white" : "bg-violet-100 text-violet-700"}`} aria-hidden="true">
          {completo ? <Check size={16} strokeWidth={2.6} /> : <Upload size={16} />}
        </span>
        <h2 id={idTitulo} className="text-lg font-semibold text-slate-900">{p.titulo}</h2>
      </div>

      {nombre ? <p className="font-mono text-sm text-slate-800 break-all">{nombre}</p> : <p className="text-sm font-medium text-slate-900">Arrastrá el archivo acá</p>}
      <p className="text-sm text-slate-600">{p.ayuda}</p>

      {subiendo && (
        <div className="space-y-1">
          <div role="progressbar" aria-label={`Subiendo ${p.titulo.toLowerCase()}`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={local!.avance}
            className="h-2 rounded-full bg-violet-100 overflow-hidden">
            <div className="h-full bg-violet-600 transition-all" style={{ width: `${local!.avance}%` }} />
          </div>
          <p className="text-xs text-slate-600 inline-flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" aria-hidden="true" /> Subiendo… {local!.avance}%</p>
        </div>
      )}
      {completo && <p className="text-sm font-semibold text-emerald-700">Subido</p>}
      {aMedias && <p className="text-sm text-amber-800">Quedó a medias ({avance(servidor)}%). Elegí el mismo archivo para seguir desde ahí.</p>}
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}

      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => input.current?.click()} disabled={p.deshabilitada}
          className="min-h-[44px] px-4 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed">
          {nombre ? "Cambiar archivo" : "Elegir archivo"}
        </button>
        {error && p.onReintentar && (
          <button type="button" onClick={p.onReintentar} disabled={p.deshabilitada}
            className="min-h-[44px] px-4 rounded-xl border border-violet-200 bg-white text-sm font-semibold text-violet-700 hover:bg-violet-50 inline-flex items-center gap-1.5 disabled:opacity-50">
            <RefreshCw size={14} aria-hidden="true" /> Reintentar
          </button>
        )}
      </div>
      <input ref={input} type="file" accept={ACEPTA} className="sr-only" tabIndex={-1} aria-label={`Archivo de ${p.titulo.toLowerCase()}`}
        onChange={(e) => { p.onArchivo(e.target.files?.[0]); e.target.value = ""; }} />
    </section>
  );
}
